import { useCan } from '@refinedev/core';
import type { ColumnDef } from '@tanstack/react-table';
import { AnimatePresence, m, type Variants } from 'framer-motion';
import {
  Building2,
  Check,
  Clock,
  History,
  KeyRound,
  Link2,
  MapPin,
  ShieldCheck,
  Tag,
  Terminal as TerminalIcon,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ErrorNotice } from '../../components/ErrorNotice';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  CodeBlock,
  ConfirmDialog,
  CopyButton,
  DataTable,
  DataTableColumnHeader,
  EmptyState,
  Field,
  Loading,
  Modal,
  Notice,
  Select,
} from '../../components/ui';
import {
  BARE_MANUAL_DEFAULT_EXPIRY_MINUTES,
  BARE_MANUAL_MAX_EXPIRY_MINUTES,
  MAX_USES_CEILING,
  MULTI_USE_MAX_EXPIRY_MINUTES,
  SHORT_CODE_DEFAULT_EXPIRY_MINUTES,
  SHORT_CODE_MAX_EXPIRY_MINUTES,
  checkEnrollmentCode,
  fetchCertificateAuthority,
  issueEnrollmentToken,
  listEnrollmentTokens,
  revokeEnrollmentToken,
  type CertificateAuthorityStatus,
  type EnrollmentCodeCheck,
  type EnrollmentTokenFormat,
  type EnrollmentTokenSummary,
  type IssuedEnrollmentToken,
} from '../../data/admin';
import { useHierarchy } from '../../hierarchy/HierarchyProvider';
import { timeAgo } from '../../lib/format';

/**
 * Emisión de credenciales de instalación (Módulo 2 §3, RF-02, y plan de
 * enrolamiento por código).
 *
 * Un token o un código de instalación **es una credencial**: quien lo tiene puede
 * meter una máquina en el portafolio de un cliente. De ahí las cinco cosas que
 * esta pantalla hace y que no son adorno:
 *
 * 1. **el valor en claro se muestra una sola vez** —el OCC guarda solo su hash— y
 *    se dice antes de cerrar el diálogo, no después;
 * 2. **el one-liner viene hecho por el backend** (`installCommandLinux` /
 *    `installCommandWindows`) y se pega tal cual, con el secreto como argumento y
 *    nunca en la URL: `GET /install.sh?token=…` acabaría en el log de acceso, en
 *    el del proxy inverso y en cualquier caché por el medio;
 * 3. **un token multiuso se anuncia como lo que es**: sigue sirviendo para meter
 *    N−1 máquinas más después de la primera, así que el tiempo es lo único que
 *    acota el daño si se filtra. El backend le impone 24 h de tope y la pantalla
 *    lo aplica antes de mandar, para que el error no llegue como un 400;
 * 4. **un código de seis caracteres se paga con caducidad**. Treinta bits no se
 *    sostienen solos (§10 del plan), así que el aleatorio dura 30 minutos por
 *    defecto con 8 h de tope, y el manual pelado —que no tiene entropía ninguna—
 *    15 minutos con 2 h de tope. La pantalla esconde lo que el backend rechazaría
 *    en vez de dejar que el formulario se envíe;
 * 5. **lo que se abre se puede cerrar.** La tabla de credenciales abiertas y su
 *    botón de revocar son la contrapartida de que un código se resuelva desde una
 *    página pública: un secreto que no se puede ver tampoco se puede apagar, y
 *    hasta ahora un token emitido se olvidaba hasta que caducaba (§11 del plan).
 *
 * **El modo por defecto es el código corto**, no el token largo. El largo sigue
 * ahí y sigue siendo lo correcto para GPO e Intune, pero el caso que duele es la
 * primera máquina de un cliente que no tiene ni dominio ni Intune, y ahí el
 * código es lo que evita el USB.
 */

const EXPIRY_OPTIONS = [
  { minutes: 15, label: '15 minutos' },
  { minutes: SHORT_CODE_DEFAULT_EXPIRY_MINUTES, label: '30 minutos' },
  { minutes: 60, label: '1 hora' },
  { minutes: BARE_MANUAL_MAX_EXPIRY_MINUTES, label: '2 horas' },
  { minutes: SHORT_CODE_MAX_EXPIRY_MINUTES, label: '8 horas' },
  { minutes: MULTI_USE_MAX_EXPIRY_MINUTES, label: '24 horas' },
  { minutes: 60 * 24 * 7, label: '7 días' },
];

/** Caducidad por defecto del token largo. La de siempre. */
const LONG_DEFAULT_EXPIRY_MINUTES = 60;

/**
 * Base32 de Crockford, para el sufijo que el panel propone.
 *
 * Es la **cuarta** copia del alfabeto —las otras tres están en
 * `enrollment-token.ts`, `install.sh` e `install.ps1`— y está aquí porque el
 * sufijo tiene que verse antes de emitir: el administrador lo lee, lo puede
 * cambiar, y `code/check` comprueba el texto final y no una etiqueta a medias.
 * No hace falta que coincida byte a byte con las otras tres, a diferencia del
 * checksum del token largo: esto solo elige cuatro caracteres al azar, y el
 * alfabeto es el mismo por el mismo motivo que allí —que se pueda dictar sin
 * confundir un uno con una ele.
 */
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const SUFFIX_CHARS = 4;

/**
 * Cuatro caracteres al azar del navegador. `crypto.getRandomValues` y no
 * `Math.random`, que no promete nada sobre lo impredecible de lo que devuelve, y
 * estos veinte bits son lo único que separa a `FRUTAL-7K2M` de `FRUTAL`.
 *
 * El `& 31` no sesga nada: 256 es múltiplo de 32, así que cada símbolo sale de
 * ocho valores de byte, los mismos ocho para todos.
 */
function proposeSuffix(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(SUFFIX_CHARS));
  return Array.from(bytes, (byte) => CROCKFORD[byte & 31]).join('');
}

/** Lo que se puede escribir en la etiqueta de un código manual. */
const LABEL_ALLOWED = /[^A-Za-z0-9-]+/g;

/**
 * Cómo se nombra cada formato en la tabla de credenciales abiertas.
 *
 * Con el número de caracteres dentro porque es lo que distingue el riesgo de un
 * vistazo: seis caracteres se adivinan mucho antes que cuarenta y uno, y un
 * manual vale lo que le puso quien lo eligió.
 */
const FORMAT_LABEL: Record<EnrollmentTokenFormat, string> = {
  short: 'Código de 6',
  manual: 'Código a mano',
  long: 'Token largo',
};

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(' ');

const MODAL_CONTAINER: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05 } },
};
const MODAL_ITEM: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: { duration: 0.2, ease: 'easeOut' } },
};

/**
 * Aparece/desaparece con alto y opacidad en vez de saltar: el formulario
 * enseña y esconde secciones enteras según lo que se vaya marcando (código a
 * mano, pelado, multiuso…), y un salto seco en cada clic se siente más torpe
 * que el formulario de antes, no más rápido.
 */
function Collapse({ show, children }: { show: boolean; children: ReactNode }) {
  return (
    <AnimatePresence initial={false}>
      {show && (
        <m.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="overflow-hidden"
        >
          {children}
        </m.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Tarjeta de elección para "qué se emite". Es la decisión que más cambia el
 * resto del formulario, así que se enseña como las dos opciones que son —no
 * como una más de un `<select>` de seis— y cada una dice de un vistazo para
 * qué sirve.
 */
function FormatChoice({
  icon: Icon,
  title,
  description,
  selected,
  onClick,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cx(
        'rounded-lg border p-3 text-left transition-colors',
        selected
          ? 'border-[var(--occ-brand)] bg-[var(--occ-brand)]/5 ring-1 ring-[var(--occ-brand)]/30'
          : 'border-black/10 hover:bg-accent/40 dark:border-white/10',
      )}
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <Icon className={cx('size-4', selected ? 'text-[var(--occ-brand)]' : 'opacity-70')} />
        {title}
      </div>
      <p className="mt-1 text-xs opacity-60">{description}</p>
    </button>
  );
}

export function EnrollmentPage() {
  const { tree, refresh } = useHierarchy();
  const { data: permission } = useCan({ resource: 'enrollment', action: 'create' });
  // Aparte del de emitir aunque hoy pidan el mismo rol: son dos capacidades y el
  // motivo que se enseña al lado del botón tiene que ser el de la que se niega.
  const { data: revokePermission } = useCan({ resource: 'enrollment', action: 'delete' });

  const [clientId, setClientId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [suggestedAlias, setSuggestedAlias] = useState('');
  const [maxUses, setMaxUses] = useState(1);

  // El código corto es el modo por defecto: el token largo sigue ahí para GPO e
  // Intune, pero el caso que duele es la primera máquina de un cliente sin nada.
  const [shortCode, setShortCode] = useState(true);
  const [manual, setManual] = useState(false);
  const [label, setLabel] = useState('');
  const [suffix, setSuffix] = useState(proposeSuffix);
  const [bare, setBare] = useState(false);
  const [bareAcknowledged, setBareAcknowledged] = useState(false);
  const [expiresInMinutes, setExpiresInMinutes] = useState(SHORT_CODE_DEFAULT_EXPIRY_MINUTES);

  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState<IssuedEnrollmentToken | null>(null);
  const [error, setError] = useState<unknown>(null);
  // Sube al emitir, y con él se vuelve a pedir la tabla de abiertas: el caso
  // normal es emitir uno de más y querer apagarlo en el mismo minuto.
  const [issuedCount, setIssuedCount] = useState(0);

  // Memorizado porque alimenta un `useMemo` y un `useEffect`: `?? []` daría un
  // array nuevo en cada render y los recalcularía sin motivo.
  const clients = useMemo(() => tree?.clients ?? [], [tree]);
  const branches = useMemo(
    () => clients.find((client) => client.id === clientId)?.branches ?? [],
    [clients, clientId],
  );

  // El primer cliente y su primera sucursal, para que el formulario sea usable
  // sin dos clics previos. Se recalcula si el árbol llega después.
  useEffect(() => {
    if (clientId === '' && clients[0]) setClientId(clients[0].id);
  }, [clients, clientId]);

  useEffect(() => {
    setBranchId(branches[0]?.id ?? '');
  }, [branches]);

  const format: EnrollmentTokenFormat = !shortCode ? 'long' : manual ? 'manual' : 'short';
  const bareManual = format === 'manual' && bare;
  const code = manual ? (bare ? label : label && `${label}-${suffix}`) : '';

  const defaultExpiry = !shortCode
    ? LONG_DEFAULT_EXPIRY_MINUTES
    : bareManual
      ? BARE_MANUAL_DEFAULT_EXPIRY_MINUTES
      : SHORT_CODE_DEFAULT_EXPIRY_MINUTES;

  // Cada modo trae su propia caducidad por defecto porque cada uno arriesga otra
  // cosa, y cambiar de modo sin cambiarla dejaría un manual pelado con las ocho
  // horas que se eligieron para un aleatorio.
  useEffect(() => {
    setExpiresInMinutes(defaultExpiry);
  }, [defaultExpiry]);

  const multiUse = maxUses > 1;
  const formatMaxExpiry = !shortCode
    ? Number.POSITIVE_INFINITY
    : bareManual
      ? BARE_MANUAL_MAX_EXPIRY_MINUTES
      : SHORT_CODE_MAX_EXPIRY_MINUTES;
  const maxExpiry = Math.min(
    formatMaxExpiry,
    multiUse ? MULTI_USE_MAX_EXPIRY_MINUTES : Number.POSITIVE_INFINITY,
  );
  const expiryTooLong = expiresInMinutes > maxExpiry;

  const [check, setCheck] = useState<EnrollmentCodeCheck | null>(null);
  const [checking, setChecking] = useState(false);

  /*
   * ¿Está libre el código manual? Se pregunta al backend mientras se escribe
   * (§13.4: la colisión se rechaza en el formulario, no en el `POST`).
   *
   * El motivo lo redacta el backend, que es quien tiene delante la lista de
   * rechazo y los códigos vivos; aquí solo se pinta. Con retardo de medio segundo
   * para no mandar una petición por tecla, y `cancelled` para que la respuesta de
   * un código ya editado no pise la del actual.
   */
  useEffect(() => {
    if (format !== 'manual' || code === '' || clientId === '' || branchId === '') {
      setCheck(null);
      setChecking(false);
      return;
    }

    let cancelled = false;
    setChecking(true);
    const timer = setTimeout(() => {
      checkEnrollmentCode({ clientId, branchId, code })
        .then((result) => !cancelled && setCheck(result))
        // Un fallo de red no puede bloquear la emisión: el backend vuelve a
        // aplicar las dos reglas al emitir, así que lo peor que pasa es un 400
        // en vez de un aviso mientras se escribe.
        .catch(() => !cancelled && setCheck(null))
        .finally(() => !cancelled && setChecking(false));
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [format, code, clientId, branchId]);

  const codeRejected = check !== null && !check.available;
  const clientName = clients.find((client) => client.id === clientId)?.name ?? 'el cliente';

  const issue = async () => {
    setIssuing(true);
    setError(null);
    try {
      const token = await issueEnrollmentToken({
        clientId,
        branchId,
        suggestedAlias: suggestedAlias.trim() || undefined,
        expiresInMinutes,
        maxUses,
        format,
        code: format === 'manual' ? code : undefined,
        // El backend no puede comprobar si esos cuatro caracteres los sorteó
        // nadie: se los declara, y de esa declaración cuelga el tope de caducidad.
        randomSuffix: format === 'manual' ? !bare : undefined,
      });
      setIssued(token);
      setIssuedCount((previous) => previous + 1);
      // El sufijo se vuelve a sortear: emitir dos códigos seguidos con el mismo
      // sufijo por no haberlo mirado es exactamente lo que la propuesta evita.
      setSuffix(proposeSuffix());
    } catch (cause) {
      setError(cause);
    } finally {
      setIssuing(false);
    }
  };

  return (
    <section className="grid gap-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Enrolar un nodo</h1>
        <p className="text-sm opacity-60">
          Emite un código de instalación y tecléalo en la máquina de destino, o un token largo si
          vas a desplegar por GPO o Intune.
        </p>
      </div>

      {!permission?.can && (
        <Alert>{permission?.reason ?? 'Solo un administrador del tenant emite tokens.'}</Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <Card
          title={
            <span className="inline-flex items-center gap-2">
              <KeyRound className="size-4 opacity-70" />
              Credencial de instalación
            </span>
          }
        >
          <form
            className="grid gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              void issue();
            }}
          >
            <div className="grid gap-2">
              <span className="text-sm opacity-70">Qué se emite</span>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <FormatChoice
                  icon={KeyRound}
                  title="Código corto"
                  description="Se teclea en la caja de destino"
                  selected={shortCode}
                  onClick={() => setShortCode(true)}
                />
                <FormatChoice
                  icon={TerminalIcon}
                  title="Token largo"
                  description="Para GPO, Intune y scripts"
                  selected={!shortCode}
                  onClick={() => setShortCode(false)}
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label="Cliente"
                icon={<Building2 className="size-4" />}
                value={clientId}
                onChange={(event) => setClientId(event.target.value)}
              >
                {clients.length === 0 && <option value="">Sin clientes</option>}
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </Select>

              <Select
                label="Sucursal"
                icon={<MapPin className="size-4" />}
                value={branchId}
                onChange={(event) => setBranchId(event.target.value)}
              >
                {branches.length === 0 && <option value="">Sin sucursales</option>}
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </Select>

              <Field
                label="Alias sugerido (opcional)"
                icon={<Tag className="size-4" />}
                value={suggestedAlias}
                placeholder="Frutal-Centro-Caja3"
                hint="El instalador puede pisarlo con --alias; entonces gana el del instalador."
                onChange={(event) => setSuggestedAlias(event.target.value)}
              />

              <Select
                label="Caduca en"
                icon={<Clock className="size-4" />}
                value={expiresInMinutes}
                onChange={(event) => setExpiresInMinutes(Number(event.target.value))}
              >
                {EXPIRY_OPTIONS.filter(
                  // Se esconde lo que el backend rechazaría —el tope del formato y
                  // el de 24 h del multiuso— en vez de dejar que el formulario se
                  // envíe para recibir un 400.
                  (option) => option.minutes <= maxExpiry,
                ).map((option) => (
                  <option key={option.minutes} value={option.minutes}>
                    {option.label}
                  </option>
                ))}
              </Select>

              <Field
                label="Usos"
                icon={<Users className="size-4" />}
                type="number"
                min={1}
                max={MAX_USES_CEILING}
                value={maxUses}
                hint={`1 para una caja concreta. Hasta ${MAX_USES_CEILING} para un despliegue masivo.`}
                onChange={(event) =>
                  setMaxUses(
                    Math.min(MAX_USES_CEILING, Math.max(1, Number(event.target.value) || 1)),
                  )
                }
              />
            </div>

            <Collapse show={shortCode}>
              <div className="grid gap-3 rounded bg-accent/30 p-3">
                <Checkbox
                  label="Fijar el código a mano"
                  checked={manual}
                  onChange={(event) => setManual(event.target.checked)}
                />
                <p className="text-xs opacity-60">
                  Sin marcar, el OCC sortea seis caracteres (<code>4K7-M9Q</code>). Marcado, lo
                  eliges tú y se puede reconocer de un vistazo — a cambio de que su fuerza sea la
                  que le pongas.
                </p>

                <Collapse show={manual}>
                  <div className="grid gap-3">
                    <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                      <Field
                        label="Etiqueta"
                        value={label}
                        placeholder="FRUTAL"
                        hint="Letras y números. Se guarda en mayúsculas."
                        onChange={(event) =>
                          setLabel(event.target.value.toUpperCase().replace(LABEL_ALLOWED, ''))
                        }
                      />
                      {!bare && (
                        <Button type="button" onClick={() => setSuffix(proposeSuffix())}>
                          Otro sufijo
                        </Button>
                      )}
                    </div>

                    <div className="flex flex-wrap items-baseline gap-2 text-sm">
                      <span className="opacity-60">Se emitirá</span>
                      <code className="font-mono text-base">{code || '—'}</code>
                      {checking && <span className="text-xs opacity-60">comprobando…</span>}
                      {!checking && check?.available && (
                        <span className="text-xs opacity-60">libre ✓</span>
                      )}
                    </div>

                    <Checkbox
                      label="Usar la etiqueta pelada, sin sufijo"
                      checked={bare}
                      onChange={(event) => {
                        setBare(event.target.checked);
                        setBareAcknowledged(false);
                      }}
                    />

                    <Collapse show={bareManual}>
                      <Notice tone="warn" title="Un código pelado no tiene entropía">
                        <p>
                          <code>{code || 'FRUTAL'}</code> se adivina al primer intento si alguien
                          sabe que estás instalando en <strong>{clientName}</strong>. Quien acierte
                          mete{' '}
                          <strong>
                            {maxUses === 1 ? 'una máquina' : `hasta ${maxUses} máquinas`}
                          </strong>{' '}
                          en esa sucursal, visibles en el panel desde el primer segundo. Por eso
                          dura como máximo <strong>2 horas</strong> y por eso hay que decir para
                          cuántas máquinas es.
                        </p>
                        <p className="mt-2">
                          Si no lo estás emitiendo con la caja delante, quítale la marca y usa{' '}
                          <code>{label ? `${label}-${suffix}` : 'FRUTAL-7K2M'}</code>: se dicta
                          igual de bien y conserva 20 bits que nadie adivina.
                        </p>
                        <div className="mt-2">
                          <Checkbox
                            label={`Lo entiendo: es para ${maxUses === 1 ? '1 máquina' : `${maxUses} máquinas`} y dura ${expiresInMinutes} minutos`}
                            checked={bareAcknowledged}
                            onChange={(event) => setBareAcknowledged(event.target.checked)}
                          />
                        </div>
                      </Notice>
                    </Collapse>

                    {codeRejected && <Alert>{check?.reason}</Alert>}
                  </div>
                </Collapse>
              </div>
            </Collapse>

            <Collapse show={multiUse}>
              <Notice tone="warn" title={`Credencial para ${maxUses} máquinas`}>
                Una de un solo uso pierde su valor al canjearse. Esta seguirá sirviendo para meter{' '}
                {maxUses - 1} máquinas más en <strong>{clientName}</strong> hasta que caduque, así
                que el backend le impone un tope de <strong>24 horas</strong> — lo que dura una
                jornada de despliegue.
              </Notice>
            </Collapse>

            <Collapse show={expiryTooLong}>
              <Alert>
                Con estas opciones la caducidad no puede pasar de {maxExpiry} minutos. Baja la
                caducidad, o cambia el formato o el cupo de usos.
              </Alert>
            </Collapse>

            {error !== null && (
              <ErrorNotice
                error={error}
                what="Emitir la credencial"
                pendingHint="`POST /enrollment/tokens` sí existe: si falla con 403, es el RBAC."
              />
            )}

            <div>
              <Button
                type="submit"
                variant="primary"
                disabled={
                  issuing ||
                  !permission?.can ||
                  clientId === '' ||
                  branchId === '' ||
                  expiryTooLong ||
                  (format === 'manual' && (code === '' || checking || codeRejected)) ||
                  (bareManual && !bareAcknowledged)
                }
              >
                <KeyRound className="size-4" />
                {issuing ? 'Emitiendo…' : shortCode ? 'Emitir código' : 'Emitir token'}
              </Button>
            </div>
          </form>
        </Card>

        <CertificateAuthorityCard />
      </div>

      <LiveTokensCard
        reloadKey={issuedCount}
        canRevoke={revokePermission?.can === true}
        revokeReason={revokePermission?.reason}
      />

      <IssuedTokenModal
        token={issued}
        onClose={() => {
          setIssued(null);
          // Un nodo enrolado aparece en el árbol; refrescar aquí ahorra al técnico
          // preguntarse si funcionó.
          refresh();
        }}
      />
    </section>
  );
}

/**
 * La credencial, una sola vez.
 *
 * No se puede cerrar con la tecla de escape ni pulsando fuera: es la única
 * oportunidad de copiarla, y un cierre accidental obliga a emitir otra y deja la
 * primera viva hasta que caduque.
 */
function IssuedTokenModal({
  token,
  onClose,
}: {
  token: IssuedEnrollmentToken | null;
  onClose: () => void;
}) {
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (token) setAcknowledged(false);
  }, [token]);

  if (!token) return null;

  const esCodigo = token.format !== 'long';

  return (
    <Modal
      open
      wide
      title={
        <span className="inline-flex items-center gap-2">
          <KeyRound className="size-4 opacity-70" />
          {esCodigo ? 'Código' : 'Token'} emitido — se muestra una sola vez
        </span>
      }
      // Un clic fuera o un Escape no cierran: solo marcan el acuse, y hace falta
      // un segundo gesto deliberado para perder el código de vista.
      onClose={() => (acknowledged ? onClose() : setAcknowledged(true))}
      footer={
        <Button
          variant={acknowledged ? 'primary' : 'secondary'}
          onClick={() => (acknowledged ? onClose() : setAcknowledged(true))}
        >
          {acknowledged ? (
            <>
              <Check className="size-4" />
              Cerrar
            </>
          ) : (
            'Ya lo copié'
          )}
        </Button>
      }
    >
      <m.div variants={MODAL_CONTAINER} initial="hidden" animate="show" className="grid gap-4">
        <m.div variants={MODAL_ITEM}>
          <Notice
            tone="warn"
            title={`El OCC solo guarda el hash de este ${esCodigo ? 'código' : 'token'}`}
          >
            En cuanto cierres este diálogo no hay forma de volver a verlo. Si lo pierdes, emite
            otro: el anterior seguirá válido hasta que caduque, así que no dejes credenciales
            sueltas.
          </Notice>
        </m.div>

        <m.div variants={MODAL_ITEM} className="flex flex-wrap items-center gap-2 text-sm">
          <Badge tone="warn">{token.maxUses === 1 ? 'un solo uso' : `${token.maxUses} usos`}</Badge>
          {token.format === 'manual' && <Badge tone="warn">código fijado a mano</Badge>}
          <span className="opacity-70">
            caduca {timeAgo(token.expiresAt)} ({token.expiresAt.toLocaleString('es')})
          </span>
        </m.div>

        <m.div variants={MODAL_ITEM}>
          {esCodigo ? (
            <CodeToType code={token.token} />
          ) : (
            <CodeBlock
              label={
                <span className="inline-flex items-center gap-1.5">
                  <KeyRound className="size-3.5" />
                  Token
                </span>
              }
              value={token.token}
            />
          )}
        </m.div>

        <m.div variants={MODAL_ITEM}>
          <CodeBlock
            label={
              <span className="inline-flex items-center gap-1.5">
                <TerminalIcon className="size-3.5" />
                Linux / macOS
              </span>
            }
            value={token.installCommandLinux}
          />
        </m.div>

        <m.div variants={MODAL_ITEM}>
          <CodeBlock
            label={
              <span className="inline-flex items-center gap-1.5">
                <TerminalIcon className="size-3.5" />
                Windows (PowerShell)
              </span>
            }
            value={token.installCommandWindows}
          />
        </m.div>

        {/*
         * Windows 7 lleva su propio comando porque el de arriba no puede funcionar
         * allí, y cuando el backend no puede darlo se dice el motivo en su sitio:
         * un hueco sin explicación acabaría con alguien pegando el comando de
         * Windows normal en una caja de 2009.
         */}
        <m.div variants={MODAL_ITEM}>
          {token.installCommandWindows7 ? (
            <CodeBlock
              label={
                <span className="inline-flex items-center gap-1.5">
                  <History className="size-3.5" />
                  Windows 7 (PowerShell 2.0)
                </span>
              }
              value={token.installCommandWindows7}
            />
          ) : (
            <Notice tone="warn" title="Sin comando de alta para Windows 7">
              {token.installCommandWindows7Unavailable ??
                'Este OCC no ofrece el comando de alta para Windows 7. Alta offline con install-win7.ps1 -LocalArtifact.'}
            </Notice>
          )}
        </m.div>

        <m.div variants={MODAL_ITEM} className="text-xs opacity-60">
          El secreto va como argumento o variable de entorno, nunca en la URL. En Windows la forma{' '}
          <code>irm | iex</code> no admite parámetros y por eso el script lee{' '}
          <code>{esCodigo ? 'OCC_CODE' : 'OCC_TOKEN'}</code> y <code>OCC_URL</code> del entorno.
        </m.div>

        {token.installCommandWindows7 && (
          <m.div variants={MODAL_ITEM} className="text-xs opacity-60">
            El de Windows 7 descarga por <code>http</code> y lleva un <code>sha256</code> dentro
            porque el PowerShell 2.0 de esas cajas no alcanza TLS 1.2 y no se puede actualizar: la
            integridad la garantiza ese hash, que copias desde aquí por HTTPS y en tu equipo, y el
            comando aborta si no cuadra. El secreto no viaja por <code>http</code>: solo lo
            transmite el binario del agente, con su propio TLS.
          </m.div>
        )}
      </m.div>
    </Modal>
  );
}

/**
 * El código, grande, con la URL que hay que teclear al lado.
 *
 * Es el elemento más grande del diálogo porque es lo único que hay que
 * transportar hasta la caja del cliente, y muchas veces se transporta leyéndolo
 * en voz alta por teléfono. La URL va **al lado y no debajo** por lo mismo: las
 * dos cosas se dictan juntas y en ese orden.
 *
 * La URL sale de `window.location.origin` y no de una variable del backend: la
 * página de códigos es una ruta de este mismo panel, así que el sitio donde hay
 * que teclear el código es exactamente el sitio desde el que se está mirando. Una
 * URL configurada aparte sería una que se puede quedar desfasada.
 */
function CodeToType({ code }: { code: string }) {
  const url = `${window.location.origin}/i`;

  return (
    <div className="grid gap-3 rounded-lg bg-[var(--occ-brand)]/5 p-4 ring-1 ring-[var(--occ-brand)]/20 sm:grid-cols-2">
      <div className="grid gap-1.5">
        <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide opacity-50">
          <KeyRound className="size-3.5" />
          Código
        </div>
        <div className="font-mono text-3xl font-semibold tracking-[0.15em] break-all">{code}</div>
        <div>
          <CopyButton value={code} label="Copiar el código" />
        </div>
      </div>

      <div className="grid gap-1.5">
        <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide opacity-50">
          <Link2 className="size-3.5" />
          En la caja de destino, abre
        </div>
        <div className="font-mono text-lg break-all">{url}</div>
        <p className="text-xs opacity-60">
          Ahí se teclea el código y la página entrega el comando ya armado, con el sistema operativo
          detectado. Si esa caja no tiene navegador, pega directamente uno de los comandos de abajo
          en una consola de administrador.
        </p>
        <div>
          <CopyButton value={url} label="Copiar la URL" />
        </div>
      </div>
    </div>
  );
}

/**
 * Los códigos y tokens que siguen abiertos, con su botón de apagarlos (§11).
 *
 * Hasta ahora una credencial emitida no se podía ni ver ni apagar: se emitía y se
 * olvidaba hasta que caducaba. Con un código de seis caracteres que se resuelve
 * desde una página pública eso pasa de ser una molestia a ser un agujero — un
 * código dictado por teléfono al técnico equivocado no se podía cerrar.
 *
 * Lo que se enseña es lo que hace falta para decidir si apagarlo: a dónde
 * instala, de qué clase es, cuánto le queda y cuántas máquinas ha metido ya. **El
 * código no está**, y no es un hueco: el OCC solo guarda su hash y no puede
 * recuperarlo. Por eso la columna de la izquierda es el destino y no el secreto.
 */
function LiveTokensCard({
  reloadKey,
  canRevoke,
  revokeReason,
}: {
  reloadKey: number;
  canRevoke: boolean;
  revokeReason?: string;
}) {
  const [tokens, setTokens] = useState<EnrollmentTokenSummary[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [revoking, setRevoking] = useState<EnrollmentTokenSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    listEnrollmentTokens()
      .then((rows) => !cancelled && setTokens(rows))
      .catch((cause: unknown) => !cancelled && setError(cause));
    return () => {
      cancelled = true;
    };
    // `reloadKey` lo sube la pantalla al emitir: el código que acaba de nacer
    // tiene que aparecer aquí sin recargar la página, porque el caso normal es
    // emitir uno de más y querer apagarlo en el mismo minuto.
  }, [reloadKey, reloads]);

  const revoke = async () => {
    if (!revoking) return;
    setBusy(true);
    setError(null);
    try {
      await revokeEnrollmentToken(revoking.id);
      setRevoking(null);
      // Se vuelve a listar en vez de quitar la fila a mano: entre que se leyó la
      // tabla y se pulsó, otro código puede haber caducado, y una tabla que
      // miente sobre lo que sigue abierto es peor que una que tarda un segundo.
      setReloads((previous) => previous + 1);
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  };

  const columns = useMemo<ColumnDef<EnrollmentTokenSummary>[]>(
    () => [
      {
        id: 'destino',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Destino" />,
        accessorFn: (token) => `${token.clientName} → ${token.branchName}`,
        meta: { label: 'Destino' },
        cell: ({ row }) => {
          const token = row.original;
          return (
            <>
              {token.clientName} → {token.branchName}
              {token.suggestedAlias && <span className="opacity-60"> · {token.suggestedAlias}</span>}
            </>
          );
        },
      },
      {
        id: 'formato',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Qué es" />,
        accessorFn: (token) => FORMAT_LABEL[token.format],
        meta: { label: 'Qué es' },
        cell: ({ row }) => (
          <Badge tone={row.original.format === 'manual' ? 'warn' : 'neutral'}>
            {FORMAT_LABEL[row.original.format]}
          </Badge>
        ),
      },
      {
        id: 'caduca',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Caduca" />,
        accessorFn: (token) => token.expiresAt.getTime(),
        meta: { label: 'Caduca' },
        cell: ({ row }) => (
          <span className="opacity-70" title={row.original.expiresAt.toLocaleString('es')}>
            {timeAgo(row.original.expiresAt)}
          </span>
        ),
      },
      {
        id: 'usos',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Usos" />,
        accessorFn: (token) => token.usedCount,
        meta: { label: 'Usos' },
        cell: ({ row }) => (
          <span className="opacity-70">
            {row.original.usedCount} / {row.original.maxUses}
          </span>
        ),
      },
      {
        id: 'emitida',
        header: ({ column }) => <DataTableColumnHeader column={column} title="La emitió" />,
        accessorFn: (token) => token.issuedByEmail ?? '—',
        meta: { label: 'La emitió' },
        cell: ({ getValue }) => <span className="opacity-70">{getValue<string>()}</span>,
      },
      {
        id: 'acciones',
        header: 'Acciones',
        meta: { label: 'Acciones' },
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button
              variant="danger"
              disabled={!canRevoke}
              title={canRevoke ? undefined : revokeReason}
              onClick={() => setRevoking(row.original)}
            >
              Revocar
            </Button>
          </div>
        ),
      },
    ],
    [canRevoke, revokeReason],
  );

  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          <KeyRound className="size-4 opacity-70" />
          Credenciales de instalación abiertas
        </span>
      }
    >
      <div className="grid gap-3">
        <p className="text-sm opacity-60">
          Todo lo que ahora mismo puede meter una máquina en un cliente. Revocar apaga el código en
          el momento: quien lo tenga apuntado recibe un rechazo en el siguiente intento.
        </p>

        {error !== null && (
          <ErrorNotice
            error={error}
            what="Listar o revocar credenciales"
            pendingHint="`GET /enrollment/tokens` y `DELETE /enrollment/tokens/:id` sí existen: si falla con 403, es el RBAC."
          />
        )}

        {tokens === null ? (
          <Loading label="Consultando los códigos vivos…" />
        ) : tokens.length === 0 ? (
          <EmptyState title="No hay ninguna credencial abierta">
            Es el estado que hay que querer: una credencial de instalación viva es una puerta
            abierta, y lo normal es que solo esté abierta mientras se instala.
          </EmptyState>
        ) : (
          <DataTable
            columns={columns}
            data={tokens}
            pageSize={0}
            emptyMessage="No hay ninguna credencial abierta."
          />
        )}
      </div>

      {/*
       * Con confirmación y sin escribir nada: el texto que confirmaría es el
       * propio código, y el código no se puede enseñar. Lo que se enseña es lo que
       * de verdad hay que mirar antes de pulsar —el destino y los usos gastados—,
       * que es donde está el error posible: apagar el código de la tienda de al
       * lado mientras el técnico está instalando.
       */}
      <ConfirmDialog
        open={revoking !== null}
        title="Revocar la credencial"
        confirmLabel="Revocar"
        busy={busy}
        onCancel={() => setRevoking(null)}
        onConfirm={() => void revoke()}
      >
        <p>
          Deja de servir de inmediato para{' '}
          <strong>
            {revoking?.clientName} → {revoking?.branchName}
          </strong>
          . Lleva {revoking?.usedCount} de {revoking?.maxUses} usos gastados y caduca{' '}
          {revoking ? timeAgo(revoking.expiresAt) : ''}.
        </p>
        <Notice tone="warn">
          Si alguien está instalando ahora mismo con este código, su siguiente intento fallará. No
          se deshace: para seguir instalando hay que emitir otro y volver a dictarlo.
        </Notice>
      </ConfirmDialog>
    </Card>
  );
}

/** Estado de la CA interna. `GET /enrollment/ca` sí existe. */
function CertificateAuthorityCard() {
  const [state, setState] = useState<CertificateAuthorityStatus | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    fetchCertificateAuthority()
      .then((result) => !cancelled && setState(result))
      .catch((cause: unknown) => !cancelled && setError(cause));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          <ShieldCheck className="size-4 opacity-70" />
          CA interna del túnel
        </span>
      }
    >
      {error !== null ? (
        <ErrorNotice error={error} what="Consultar la CA" />
      ) : !state ? (
        <Loading label="Consultando la CA…" />
      ) : (
        <div className="grid gap-2 text-sm">
          {state.renewalDue && (
            <Notice tone="warn" title="Toca rotar la CA">
              Los certificados que emite caducan con ella. Rotarla no echa a nadie: la anterior
              sigue en el paquete de confianza hasta que caduquen los suyos.
            </Notice>
          )}
          <dl className="grid grid-cols-[8rem_1fr] gap-y-1">
            <dt className="opacity-60">Nombre</dt>
            <dd className="break-all">{state.commonName}</dd>
            <dt className="opacity-60">Serie</dt>
            <dd className="break-all font-mono text-xs">{state.serialNumber}</dd>
            <dt className="opacity-60">Caduca</dt>
            <dd>
              {state.notAfter ? state.notAfter.toLocaleDateString('es') : '—'}
              {state.expiresInDays !== null && (
                <span className="opacity-60"> ({state.expiresInDays} días)</span>
              )}
            </dd>
            <dt className="opacity-60">CA retiradas</dt>
            <dd>{state.retainedAuthorities}</dd>
          </dl>
        </div>
      )}
    </Card>
  );
}
